/**
 * Harvest marked form fields (metadata / custom_data) from container(s) into a flat object.
 * Shared internals for get_metadata_form_fields / get_custom_data_form_fields.
 *
 * Checkboxes submit their boolean checked state. Every other input
 * (text/hidden/tel/...) keeps `.value`, unless it declares a type via the
 * `data-hub-value-type` marker ("boolean" | "number", stamped by the hidden-field
 * renderer) — declared types coerce, everything else stays a string (no sniffing).
 *
 * @param {HTMLElement|NodeList|Array} form_container - Container element(s).
 * @param {string} selector - Marker attribute selector for the fields to harvest.
 * @returns {Object} Flat key/value object ({} when nothing matches).
 */
function harvest_marked_fields(form_container, selector) {
    let harvested = {};

    // Normalize to iterable: wrap single element in array
    const containers = (form_container instanceof NodeList || form_container.forEach)
        ? form_container
        : [form_container];

    containers.forEach(container => {
        const fields = container.querySelectorAll(selector);
        if (fields && fields.length > 0) {
            fields.forEach(field => {
                const key = field.id || field.name;
                if (!key) return;
                if (field.type === 'checkbox') {
                    harvested[key] = field.checked;
                } else {
                    const declared_value_type = field.getAttribute('data-hub-value-type');
                    if (declared_value_type === 'boolean') {
                        harvested[key] = field.value === 'true';
                    } else if (declared_value_type === 'number') {
                        const parsed_number = parseFloat(field.value);
                        harvested[key] = Number.isNaN(parsed_number) ? field.value : parsed_number;
                    } else {
                        harvested[key] = field.value;
                    }
                }
            });
        }
    });

    return harvested;
}

/**
 * Retrieves metadata form fields from the given container.
 * NOTE: the selector lives on the data_hub_form instance (set in init from
 * settings.metadata_form_field_selector / default_selectors) — pass the FORM instance,
 * not the DataHub instance.
 * @param {HTMLElement|NodeList} form_container - Container element(s) for the form.
 * @returns {Object} An object of metadata key/value pairs.
 */
export function get_metadata_form_fields(form_container, data_hub_instance = window?.data_hub) {
    if (!form_container) {
        form_container = data_hub_instance?.form_containers
                        || document.querySelectorAll(data_hub_instance?.form_container_selector
                        || '.data-hub-form-container');
    }

    if (!data_hub_instance || !data_hub_instance.form_metadata_form_field_selector) {
        return {};
    }

    return harvest_marked_fields(form_container, data_hub_instance.form_metadata_form_field_selector);
}

/**
 * Retrieves custom_data form fields (data-hub-form-custom-data-field markers) from the
 * given container. Mirrors get_metadata_form_fields; same instance-passing rule applies.
 * @param {HTMLElement|NodeList} form_container - Container element(s) for the form.
 * @returns {Object} An object of custom_data key/value pairs.
 */
export function get_custom_data_form_fields(form_container, data_hub_instance = window?.data_hub) {
    if (!form_container) {
        form_container = data_hub_instance?.form_containers
                        || document.querySelectorAll(data_hub_instance?.form_container_selector
                        || '.data-hub-form-container');
    }

    if (!data_hub_instance || !data_hub_instance.form_custom_data_form_field_selector) {
        return {};
    }

    return harvest_marked_fields(form_container, data_hub_instance.form_custom_data_form_field_selector);
}
